import React from 'react';
import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded } from '@mui/icons-material';
import DeleteForeverOutlined from '@mui/icons-material/DeleteForeverOutlined';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';
import constants from 'style/constants';

import WanderingDetectionConfiguration from 'features/residents/models/configurations/wandering-detection-configuration';
import WanderingDetectionTimeFrameConfiguration from 'features/residents/models/configurations/wandering-detection-time-frame-configuration';
import WanderingDetectionSettings from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/wandering-detection-scenario-configuration/wandering-detection-settings/wandering-detection-settings';
import { useWanderingDetectionTimeFrames } from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/wandering-detection-scenario-configuration/wandering-detection-time-frame-configuration/hooks';
import styles from './wandering-detection-time-frames.module.scss';

type Props = {
  wanderingDetectionConfiguration: WanderingDetectionConfiguration;
  activeAllDay: boolean;
  valuesChanged: (newTimeFrameConfigurations: WanderingDetectionTimeFrameConfiguration[] | null, newActiveAllDay: boolean) => void;
  index: number;
  timeFramesDescription: string;
  updateResidentSettingEditFormScenario: () => void;
};

const WanderingDetectionTimeFrames = (props: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');
  const {
    viewingMode,
    validationErrors,
    timePickerLocaleText,
    addTimeFrameConfiguration,
    removeTimeFrameConfiguration,
    onTimeFrameChanged,
    setAllDayTimeFrame,
    shouldShowDiscontinuityAlert,
  } = useWanderingDetectionTimeFrames(props);

  const timerPickerPopperSx = {
    '& .MuiPaper-root .MuiPickersLayout-root .MuiPickersLayout-contentWrapper .MuiMultiSectionDigitalClock-root': {
      padding: '4px',
      borderRadius: 0,
      minWidth: 0,
      margin: 0,
      marginLeft: 'auto',
      marginRight: 'auto',
      '& .MuiList-root': {
        width: '92px',
        overflowY: 'visible',
        '&::after': {
          height: '10px',
        },
        '& .MuiButtonBase-root': {
          padding: '4px',
          borderRadius: 0,
          minWidth: 0,
          margin: 0,
          marginLeft: 'auto',
          marginRight: 'auto',
        },
      },
    },
  };

  const renderTimeFrameRow = (timeFrameConfiguration: WanderingDetectionTimeFrameConfiguration, index: number) => (
    <div key={`TimeFrame-${index}`} className={styles.timeFrameRow}>
      <div className={styles.timePickersContainer}>
        <div className={styles.timePickers}>
          <TimePicker
            label={t('scenarioConfiguration.from')}
            ampm={false}
            view={'minutes'}
            views={['hours', 'minutes']}
            format="HH:mm"
            localeText={timePickerLocaleText}
            value={moment(timeFrameConfiguration.timeFrame.from, 'HH:mm:ss')}
            onChange={(value) => onTimeFrameChanged(value, index, true)}
            disabled={viewingMode === 'viewing'}
            className={`${styles.timePicker}${validationErrors[index] ? ' ' + styles.validationError : ''}`}
            slotProps={{
              popper: {
                sx: timerPickerPopperSx,
              },
            }}
          />
          <TimePicker
            label={t('scenarioConfiguration.to')}
            ampm={false}
            view={'minutes'}
            views={['hours', 'minutes']}
            format="HH:mm"
            localeText={timePickerLocaleText}
            value={moment(timeFrameConfiguration.timeFrame.to, 'HH:mm:ss')}
            onChange={(value) => onTimeFrameChanged(value, index, false)}
            disabled={viewingMode === 'viewing'}
            className={`${styles.timePicker}${validationErrors[index] ? ' ' + styles.validationError : ''}`}
            slotProps={{
              popper: {
                sx: timerPickerPopperSx,
              },
            }}
          />
          {viewingMode !== 'viewing' &&
            props.wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations &&
            props.wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations.length > 1 && (
              <DeleteForeverOutlined
                onClick={() => removeTimeFrameConfiguration(index)}
                className={`${styles.deleteIcon}${validationErrors[index] ? ' ' + styles.validationError : ''}`}
              />
            )}
        </div>
        {validationErrors[index] && (
          <div className={styles.validationText}>
            <Typography variant="caption" sx={{ color: constants.Colors.error }}>
              {t('scenarioConfiguration.timeFrameValidation')}
            </Typography>
          </div>
        )}
      </div>
      <WanderingDetectionSettings
        wanderingDetectionConfiguration={props.wanderingDetectionConfiguration}
        activeAllDay={false}
        index={index}
        valuesChanged={props.valuesChanged}
        updateResidentSettingEditFormScenario={props.updateResidentSettingEditFormScenario}
        selectedWanderingBoundaryIds={timeFrameConfiguration.wanderingBoundaryLocationIds}
      />
    </div>
  );

  return (
    <div className={styles.container}>
      <Stack>
        <Typography variant="subtitle1">{props.timeFramesDescription}</Typography>

        <FormControl
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Checkbox checked={props.activeAllDay} onChange={setAllDayTimeFrame} disabled={viewingMode === 'viewing'} />
          <Typography variant="subtitle1">{t('scenarioConfiguration.scenarioAlwaysActive')}</Typography>
        </FormControl>

        {shouldShowDiscontinuityAlert && (
          <Typography variant="subtitle1" color="error">
            {t('scenarioConfiguration.timeFrameDiscontinuityAlert')}
          </Typography>
        )}
      </Stack>

      {!props.activeAllDay && props.wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations?.map(renderTimeFrameRow)}

      {viewingMode !== 'viewing' && !props.activeAllDay && (
        <Button
          startIcon={<AddCircleOutlineRounded color="primary" />}
          variant="text"
          onClick={addTimeFrameConfiguration}
          onFocus={(event) => event.stopPropagation()}
          className={styles.addTimeFrame}
        >
          {t('scenarioConfiguration.addTimeFrame')}
        </Button>
      )}
    </div>
  );
};

export default WanderingDetectionTimeFrames;
